import { UserExtras } from "./UserExtras";
import { SigningPermCard } from "../approvals/SigningPermCard";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Eye, Save, Crown, Mail, Smartphone } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "../ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Button } from "../ui/button";
import { Switch } from "../ui/switch";
import { api } from "../../lib/api";
import { PermMatrix } from "./PermMatrix";

const NONE = "__none";
const SCOPE_AR = { all: "كل الفروع", branches: "فروعه المحددة", department: "قسمه فقط" };

export const previewUserOf = (u, role, matrix, custom = false) => {
  if (!role || (role.system && !custom)) return { name: u.name, role: role?.id || "user", module_perms: {}, can_edit: role?.id !== "user", org_wide: false, perm_matrix: {} };
  const mp = Object.fromEntries(Object.entries(matrix).map(([k, r]) => [k, ["add", "edit", "delete", "approve"].some((a) => r[a]) ? "edit" : "view"]));
  mp.overview = mp.overview || "view";
  return { name: u.name, role: "user", module_perms: mp, can_edit: Object.values(mp).includes("edit"), org_wide: role.scope === "all", perm_matrix: matrix };
};

const Field = ({ label, children }) => <div className="space-y-1"><p className="text-xs font-semibold text-slate-500">{label}</p>{children}</div>;

export const UserAccessSheet = ({ u, ctx, t, onClose, reload, onPreview }) => {
  const [dept, setDept] = useState("");
  const [name, setName] = useState("");
  const [title, setTitle] = useState("");
  const [roleId, setRoleId] = useState("user");
  const [custom, setCustom] = useState(false);
  const [matrix, setMatrix] = useState({});
  const [saving, setSaving] = useState(false);
  const role = ctx.roles.find((r) => r.id === roleId);
  useEffect(() => {
    if (!u) return;
    setDept(u.department_id || ""); setName(u.name || ""); setTitle(u.job_title_id || ""); setRoleId(u.role_id || u.role || "user");
    setCustom(Boolean(u.perm_custom));
    const r = ctx.roles.find((x) => x.id === (u.role_id || u.role));
    setMatrix(u.perm_custom ? u.perm_matrix : (r?.perms || {}));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [u?.uid]);
  const titles = useMemo(() => ctx.job_titles.filter((j) => !j.department_id || j.department_id === dept), [ctx.job_titles, dept]);
  if (!u) return null;
  const locked = u.is_primary_owner;
  const canCustom = !locked && roleId !== "manager";
  const pickRole = (id) => { setRoleId(id); setCustom(false); setMatrix(ctx.roles.find((r) => r.id === id)?.perms || {}); };
  const needBranch = roleId !== "manager" && role?.scope === "branches" && !(u.allowed_platforms || []).length;
  const save = async () => {
    if (!locked && needBranch && !(u.allowed_branches || []).length) { toast.error("اختر فرعاً واحداً على الأقل من «الفروع المسموحة» بالأسفل — بدونه ما يشوف المستخدم أي بيانات"); return; }
    if (!locked && role?.scope === "department" && !dept) { toast.error("هذا الدور نطاقه «قسمه فقط» — اختر قسماً للمستخدم"); return; }
    setSaving(true);
    try {
      const nm = name.trim() !== (u.name || "") ? { name: name.trim() } : {};
      if (nm.name !== undefined || dept !== (u.department_id || "") || title !== (u.job_title_id || "")) await api.orgPlaceUser(u.uid, { department_id: dept, job_title_id: title, ...nm });
      const roleChanged = roleId !== (u.role_id || u.role) || custom !== Boolean(u.perm_custom) || custom;
      if (roleChanged && !locked) await api.orgSetAccess(u.uid, { role_id: roleId, perms: canCustom && custom ? matrix : null });
      toast.success("تم حفظ بيانات المستخدم"); reload(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "تعذّر الحفظ"); } finally { setSaving(false); }
  };
  return (
    <Sheet open={Boolean(u)} onOpenChange={(v) => !v && onClose()}>
      <SheetContent side="left" className="w-full overflow-y-auto sm:max-w-3xl" data-testid="user-access-sheet">
        <SheetHeader className="text-start">
          <SheetTitle className="flex items-center gap-2 text-xl">{u.name}{u.is_primary_owner && <Crown className="h-4 w-4 text-amber-500" />}</SheetTitle>
          <SheetDescription className="flex flex-wrap gap-3 text-xs">
            {u.email && !u.email.endsWith(".local") && <span className="flex items-center gap-1"><Mail className="h-3 w-3" /> {u.email}</span>}
            {u.phone && <span className="flex items-center gap-1" dir="ltr"><Smartphone className="h-3 w-3" /> {u.phone}</span>}
          </SheetDescription>
        </SheetHeader>
        <div className="mt-6">
          <Field label="الاسم">
            <input value={name} onChange={(e) => setName(e.target.value)} maxLength={80} data-testid="access-name-input"
              className="h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm focus:border-blue-400 focus:outline-none" />
          </Field>
        </div>
        <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-3">
          <Field label="القسم">
            <Select value={dept || NONE} onValueChange={(v) => setDept(v === NONE ? "" : v)}>
              <SelectTrigger data-testid="access-dept-select"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value={NONE}>بدون قسم</SelectItem>{ctx.departments.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="المسمى الوظيفي">
            <Select value={title || NONE} onValueChange={(v) => setTitle(v === NONE ? "" : v)}>
              <SelectTrigger data-testid="access-title-select"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value={NONE}>بدون مسمى</SelectItem>{titles.map((j) => <SelectItem key={j.id} value={j.id}>{j.name}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="الدور">
            <Select value={roleId} onValueChange={pickRole} disabled={locked}>
              <SelectTrigger data-testid="access-role-select"><SelectValue /></SelectTrigger>
              <SelectContent>{ctx.roles.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}{r.system ? "" : " ★"}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
        </div>
        {locked && <p className="mt-3 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800">المالك الأساسي للشركة يملك كل الصلاحيات دائماً.</p>}
        {!locked && needBranch && !(u.allowed_branches || []).length && <p className="mt-3 rounded-lg bg-red-50 px-3 py-2 text-xs font-semibold text-red-700" data-testid="access-no-branch-warning">⚠️ هذا المستخدم ما عنده أي فرع — ما راح يشوف أي بيانات. اختر فروعه من «الفروع المسموحة» بالأسفل.</p>}
        {!locked && role?.scope === "department" && !dept && <p className="mt-3 rounded-lg bg-red-50 px-3 py-2 text-xs font-semibold text-red-700" data-testid="access-no-dept-warning">⚠️ نطاق الدور «قسمه فقط» — اختر قسماً للمستخدم وإلا ما راح يشوف أي بيانات.</p>}
        <div className="mt-6 flex flex-wrap items-center justify-between gap-2">
          <div>
            <p className="text-sm font-bold text-slate-800">مصفوفة الصلاحيات</p>
            <p className="text-xs text-slate-500">النطاق: {SCOPE_AR[role?.scope] || "—"}{role?.scope === "department" && !dept ? " — اختر قسماً للمستخدم" : ""}</p>
          </div>
          {canCustom && (
            <label className="flex items-center gap-2 text-xs text-slate-600">تخصيص لهذا المستخدم فقط
              <Switch checked={custom} onCheckedChange={(v) => { setCustom(v); if (!v) setMatrix(role?.perms || {}); }} data-testid="access-custom-switch" />
            </label>
          )}
        </div>
        <div className="mt-3">
          {canCustom && !custom && <p className="mb-2 rounded-lg bg-blue-50 px-3 py-2 text-xs text-blue-800">اضغط على أي خانة لتعديل صلاحيات هذا المستخدم — يتفعّل «التخصيص» تلقائياً بناءً على دوره الحالي.</p>}
          <PermMatrix value={matrix} onChange={canCustom ? (m) => { setCustom(true); setMatrix(m); } : null} modules={ctx.modules} t={t} testid="user-matrix"
            lockedMsg={roleId === "manager" ? "دور «مدير» يملك كل الصلاحيات — اختر دوراً آخر لتقييده" : "المالك الأساسي يملك كل الصلاحيات"} />
        </div>
        {u.uid && <SigningPermCard uid={u.uid} />}
        <div className="sticky bottom-0 mt-6 flex flex-wrap gap-2 border-t border-slate-100 bg-white py-4">
          <Button onClick={save} disabled={saving} className="gap-1.5" data-testid="access-save-btn"><Save className="h-4 w-4" /> حفظ</Button>
          <Button variant="outline" className="gap-1.5" onClick={() => onPreview(previewUserOf(u, role, matrix, custom))} disabled={locked} data-testid="access-preview-btn"><Eye className="h-4 w-4" /> شاهد كأنك هذا المستخدم</Button>
        </div>
        {!locked && <UserExtras u={u} reload={reload} onClose={onClose} needBranch={needBranch} />}
        <div className="h-6" />
      </SheetContent>
    </Sheet>
  );
};
